import { SWRConfig } from "swr";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ListIcon from "@mui/icons-material/List";
import MapIcon from "@mui/icons-material/Map";
import {
  Button,
  IconButton,
  LinearProgress,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";

import { fakeHotelData } from "@/_mocks/fakeHotelData";
import { ApiHotelItem, getHotelBatches, requestHotelBook } from "@/api/hotel";
import AlertModal from "@/components/AlertModal";
import IntroHotels from "@/components/IntroHotels";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import { AlertTypeEnum } from "@/constants";
import { ROUTES_PAGE_CHANGE_KEYWORDS } from "@/constants/eventName";
import useEvent from "@/hooks/useEvent";
import { dispatchEvent } from "@/hooks/useEventEmitter";
import useHotelSearchCondition from "@/hooks/useHotelSearchCondition";
import useMore from "@/hooks/useMore";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";

import FilterBar from "./components/FilterBar";
import HotelList from "./components/HotelList";
import HotelListSkeleton from "./components/HotelListSkeleton";
import HotelMapBox from "./components/HotelMapBox";
import MobileSearchBar from "./components/MobileSearchBar";
import SearchBar from "./components/SearchBar";
import Sort from "./components/Sort";
import { sortFn } from "./components/Sort/sortConfig";

const HotelPage = () => {
  const modalRef = useRef<typeof AlertModal>();
  const theme = useThemeData();

  const { searchType } = useHotelSearchCondition("hotel");

  const [searchData, setSearchData] = useState<ApiHotelItem[]>([]);
  const [filterData, setFilterData] = useState<ApiHotelItem[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [totalTitle, setTotalTitle] = useState("");
  const [sortKey, setSortKey] = useState<number>(searchType === "all" ? 1 : 0);
  const [keywords, setKeywords] = useState("");

  // const fistLoadAll = useRef(false);
  // const locked = useRef(false);

  const isPc = useMediaQuery("(min-width:1230px)");
  const { isMobile } = usePlatform();

  const [viewType, setViewType] = useState<"list" | "map">("list");

  const { historyData, handleNext, hasMore } = useMore(filterData, { pageSize: 100 });

  useEffect(() => {
    /**
     * 搜索类型变化后默认过滤项重置
     */
    setTimeout(() => {
      setSortKey(searchType === "all" ? 1 : 0);
    }, 3000);
  }, [searchType]);

  const handleSearchHotelBook = useEvent(async (value) => {
    try {
      setLoading(true);
      const res = await requestHotelBook({
        date_from: value.startDate,
        date_to: value.endDate,
        items: value.items,
      });
      if (res.data) {
        setSearchData(res.data);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 200);
    }
  });

  const handleSearchAll = useEvent(async () => {
    setLoading(true);
    getHotelBatches({
      appendData: (value) => {
        setSearchData((preState) => {
          return [...preState, ...value];
        });
      },
      complete: () => {
        setTimeout(() => {
          setLoading(false);
          // setTimeout(() => {
          //   fistLoadAll.current = false;
          // }, 1000);
        }, 200);
      },
    });

    // setSearchData(fakeHotelData.data);
    // setTimeout(() => {
    //   setLoading(false);

    //   setTimeout(() => {
    //     fistLoadAll.current = false;
    //   }, 1000);
    // }, 1200);
  });

  const sortChange = useCallback(
    (sortKey: number) => {
      const sortFilterData = sortFn(filterData, sortKey);
      setFilterData(sortFilterData);
      setSortKey(sortKey);
    },
    [filterData]
  );

  const filterChange = useCallback(
    (v: ApiHotelItem[]) => {
      const sortFilterData = sortFn(v, sortKey);
      setFilterData(sortFilterData);

      // if (!fistLoadAll.current && locked.current) {
      //   setTimeout(() => {
      //     locked.current = false;
      //   }, 1000);
      // }
    },
    [sortKey]
  );

  useEffect(() => {
    let text = "";
    if (searchData.length > 0) {
      if (filterData.length === searchData.length) {
        text =
          `${Number(searchData.length).toLocaleString()}` +
          (Number(searchData.length) == 1 ? " hotel" : " hotels");
      } else {
        text =
          `${Number(filterData.length).toLocaleString()} / ${Number(
            searchData.length
          ).toLocaleString()}` + (Number(filterData.length) == 1 ? " hotel" : " hotels");
      }
    }
    // FIXME: 延迟更新，防止闪烁，暂时没有想到更优的方式
    const timer = setTimeout(() => {
      setTotalTitle(text);
    }, 50);

    return () => {
      clearTimeout(timer);
    };
  }, [searchData, filterData]);

  const handleCreateAlert = useEvent((data: ApiHotelItem) => {
    modalRef.current?.show(AlertTypeEnum.Hotel, {
      alert_type: AlertTypeEnum.Hotel,
      fromLocation: [data.id],
      toLocation: [data.id],
      hotelName: [data.name],
    });
  });

  const renderMapData = useMemo(() => {
    // if (searchType === "all" && fistLoadAll.current && locked) {
    //   return [];
    // }
    return filterData.slice(0, 200);
  }, [filterData]);

  return (
    <SWRConfig>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | Hotel</title>
        <meta
          name="description"
          content="Elevate your award travel experience with selected top-notch hotel stays."
        />
      </Helmet>

      <div className="w-full flex">
        <div className={isPc ? "w-[55%]" : "w-full"}>
          <div className="px-[10px] mt-[16px]">
            {isMobile ? (
              <MobileSearchBar
                searchLoading={isLoading}
                onSearch={handleSearchHotelBook}
                onSearchAll={handleSearchAll}
              />
            ) : (
              <SearchBar
                searchLoading={isLoading}
                onSearch={handleSearchHotelBook}
                onSearchAll={handleSearchAll}
              />
            )}
          </div>

          <div className="px-[10px] mt-[16px]">
            <FilterBar dataSourceList={searchData} onChange={filterChange} />
          </div>
          <IntroHotels />

          {isLoading ? (
            <div className="mt-[9px] mx-[10px]">
              <LinearProgress />
            </div>
          ) : null}

          <div className="px-[10px]">
            <div className="flex items-center py-[12px] mb-[-6px]">
              <Typography variant="body1" color="textPrimary">
                {totalTitle}
              </Typography>
              <div className="flex-1" />
              {isPc && (
                <TextField
                  variant="outlined"
                  placeholder="e.g hotel name, address"
                  value={keywords}
                  size="small"
                  onChange={(e) => {
                    setKeywords(e.target.value);
                    dispatchEvent(ROUTES_PAGE_CHANGE_KEYWORDS, e.target.value);
                  }}
                  className="mr-[10px] w-[300px]"
                />
              )}
              <Sort value={sortKey} onChange={sortChange} />
            </div>
            <HotelList data={historyData} onAlert={handleCreateAlert} />

            {hasMore ? (
              <div
                className="w-full flex justify-center items-center my-[10px]"
                onClick={handleNext}
              >
                <IconButton color="primary">
                  <ExpandMoreIcon />
                </IconButton>
                <Typography color="textPrimary">More Hotels</Typography>
              </div>
            ) : null}
          </div>

          {filterData.length === 0 && !isLoading ? (
            <div className="mt-[80px] text-center">
              <Typography color="textSecondary">No options matching your filters</Typography>
              <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
            </div>
          ) : (
            <div className="px-[10px]">{isLoading ? <HotelListSkeleton count={10} /> : null}</div>
          )}
        </div>
        {isPc && (
          <div className="w-[45%] h-[100vh] fixed top-[64px] right-0">
            <HotelMapBox data={renderMapData} />
          </div>
        )}
      </div>

      <AlertModal
        ref={modalRef}
        onReset={() => {
          console.log("reset");
        }}
      />

      {isMobile && viewType === "map" && (
        <div className="fixed bottom-0 top-0 left-0 right-0 bg-white z-[10000] flex flex-col">
          <div className="pb-[10px]">
            <MobileSearchBar
              searchLoading={isLoading}
              onSearch={handleSearchHotelBook}
              onSearchAll={handleSearchAll}
            />
          </div>
          <div className="flex-1 min-h-[1px]">
            <HotelMapBox data={renderMapData} />
          </div>
        </div>
      )}

      {isMobile && searchData.length && (
        <div
          className={`z-[10000] fixed left-[50%] translate-x-[-50%] ${
            viewType === "list" ? "bottom-[10px]" : "bottom-[276px]"
          }`}
        >
          {viewType === "list" ? (
            <Button variant="contained" startIcon={<ListIcon />} onClick={() => setViewType("map")}>
              View map
            </Button>
          ) : (
            <Button variant="contained" startIcon={<MapIcon />} onClick={() => setViewType("list")}>
              View list
            </Button>
          )}
        </div>
      )}

      {viewType === "list" && <ScrollTopBar />}
    </SWRConfig>
  );
};

export default HotelPage;
